import React from "react";
import { NeedTimeslice } from "../data/need";
import { NutriComponent } from "./NutriComponent";
import { emptyNutriValue, NutriValues, nutriLookup } from "../data/nutriValues";
import { nutriValueSumReducer } from "../utils/planUtils";
import { FLEX_MARGIN, TrackingMode } from "../data/settings";

// ─── Flex-Bereich: Kcal-Linie ────────────────────────────────────────────────
//
// Aufbau:
//   • Graue Linie von 0 bis scaleMax = max(target, value) * 1.2
//   • Blauer "Guter Bereich" von target*0.90 bis target*1.125
//   • Punkt (dot) an der Stelle des aktuellen Werts
//   • Ist der Wert außerhalb des guten Bereichs → Dot bleibt grau/neutral

const FLEX_LOWER = 0.9; // -10 % vom Zielwert
const FLEX_UPPER = 0.125; // +12,5 % vom Zielwert (= FLEX_MARGIN)

function FlexKcalBar({ value, target }: { value: number; target: number }) {
  const goodFrom = target * FLEX_LOWER;
  const goodTo = target * (1 + FLEX_UPPER);
  const scaleMax = Math.max(target, value) * 1.2;

  // Hilfsfunktion: Prozentwert auf der Gesamtskala
  const pct = (v: number) => Math.max(0, Math.min(100, (v / scaleMax) * 100));

  const dotPct = pct(value);
  const inGoodZone = value >= goodFrom && value <= goodTo;

  return (
    <div className="w-full">
      {/* Wert-Label zentriert über dem Dot */}
      <div className="relative h-4 mb-1">
        <span
          className="absolute -translate-x-1/2 text-xs font-bold text-ink"
          style={{ left: `${dotPct}%` }}
        >
          {Math.round(value)}
        </span>
      </div>

      {/* Track */}
      <div className="relative h-2 rounded-full bg-selection">
        {/* Guter Bereich (blau) */}
        <div
          className="absolute h-full bg-blue/30 rounded-xs"
          style={{
            left: `${pct(goodFrom)}%`,
            width: `${pct(goodTo) - pct(goodFrom)}%`,
          }}
        />
        {/* Solide blaue Füllung nur innerhalb des guten Bereichs */}
        {inGoodZone && (
          <div
            className="absolute h-full bg-blue/70 rounded-xs"
            style={{
              left: `${pct(goodFrom)}%`,
              width: `${dotPct - pct(goodFrom)}%`,
            }}
          />
        )}

        {/* Zielwert-Markierung */}
        <div
          className="absolute w-px h-4 bg-ink-muted/50 top-[-4px]"
          style={{ left: `${pct(target)}%` }}
        />

        {/* Dot: blau wenn im guten Bereich, sonst grau */}
        <div
          className={`absolute w-3 h-3 rounded-full top-[-2px] -translate-x-1/2 border-2 bg-surface-card transition-all ${
            inGoodZone ? "border-blue" : "border-ink-muted"
          }`}
          style={{ left: `${Math.min(dotPct, 97)}%` }}
        />
      </div>

      {/* Skala-Labels */}
      <div className="flex justify-between text-xs text-ink-muted mt-1">
        <span>0</span>
        <span>{Math.round(scaleMax)}</span>
      </div>
    </div>
  );
}

// ─── Flex-Bereich: Makro-Balken ───────────────────────────────────────────────

const MACRO_COLORS: Record<string, string> = {
  fat: nutriLookup.fat.color,
  carbs: nutriLookup.carbs.color,
  protein: nutriLookup.protein.color,
};

const MACRO_LABELS: Record<string, string> = {
  fat: nutriLookup.fat.shortName,
  carbs: nutriLookup.carbs.shortName,
  protein: nutriLookup.protein.shortName,
};

function FlexMacroBar({
  name,
  value,
  target,
}: {
  name: "fat" | "carbs" | "protein";
  value: number;
  target: number;
}) {
  const min = target;
  const max = target;
  // Balken zeigt Anteil am oberen Flexwert
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const isOver = value > max;

  return (
    <div className="flex items-center gap-1 w-full">
      <span
        className="text-xs font-bold w-5 shrink-0"
        style={{ color: MACRO_COLORS[name] }}
      >
        {MACRO_LABELS[name]}
      </span>
      <div className="relative flex-1 h-1.5 rounded-full bg-surface-raised">
        {/* Flex-Zone hell (min bis max) */}
        <div
          className="absolute h-full rounded-full opacity-20"
          style={{
            backgroundColor: MACRO_COLORS[name],
            left: `${(min / max) * 100}%`,
            width: `${((max - min) / max) * 100}%`,
          }}
        />
        {/* Füll-Balken */}
        <div
          className="absolute h-full rounded-full transition-all"
          style={{
            backgroundColor: MACRO_COLORS[name],
            width: `${pct}%`,
            opacity: isOver ? 1 : 0.6,
          }}
        />
      </div>
      <span className="text-xs text-ink-muted w-12 text-right shrink-0">
        {Math.round(value)}/{Math.round(target)}
      </span>
    </div>
  );
}

// ─── Flex-Bereich: Gesamtdarstellung ─────────────────────────────────────────

function FlexNutriValues({
  sum,
  need,
}: {
  sum: NutriValues;
  need: NeedTimeslice;
}) {
  return (
    <div className="w-full flex flex-col gap-3">
      <FlexKcalBar value={sum.kcal} target={need.kcal} />
      <div className="flex flex-col gap-1.5 pt-1">
        <FlexMacroBar name="fat" value={sum.fat} target={need.fat} />
        <FlexMacroBar name="carbs" value={sum.carbs} target={need.carbs} />
        <FlexMacroBar
          name="protein"
          value={sum.protein}
          target={need.protein}
        />
      </div>
    </div>
  );
}

// ─── Balance Score ────────────────────────────────────────────────────────────

function macroScore(value: number, target: number): number {
  if (target <= 0) return 100;
  const ratio = value / target;
  // Vollpunkte bei 90–110 %, linear abfallend nach außen
  if (ratio >= 0.9 && ratio <= 1.1) return 100;
  if (ratio < 0.9) return Math.max(0, Math.round((ratio / 0.9) * 100));
  // Über 110 %: je 10 % über Ziel → -20 Punkte
  return Math.max(0, Math.round(100 - (ratio - 1.1) * 200));
}

function scoreColor(score: number): string {
  if (score >= 75) return "text-teal";
  if (score >= 50) return "text-gold";
  return "text-protein";
}

function scoreBg(score: number): string {
  if (score >= 75) return "bg-teal/10 border-teal/30";
  if (score >= 50) return "bg-gold/10 border-gold/30";
  return "bg-protein/10 border-protein/30";
}

const BALANCE_MACRO_LABELS: Record<string, { label: string; color: string }> = {
  kcal: { label: nutriLookup.kcal.name, color: nutriLookup.kcal.color },
  fat: { label: nutriLookup.fat.name, color: nutriLookup.fat.color },
  carbs: { label: nutriLookup.carbs.name, color: nutriLookup.carbs.color },
  protein: {
    label: nutriLookup.protein.name,
    color: nutriLookup.protein.color,
  },
};

function BalanceNutriValues({
  sum,
  need,
}: {
  sum: NutriValues;
  need: NeedTimeslice;
}) {
  const scores = {
    kcal: macroScore(sum.kcal, need.kcal),
    fat: macroScore(sum.fat, need.fat),
    carbs: macroScore(sum.carbs, need.carbs),
    protein: macroScore(sum.protein, need.protein),
  };
  const overall = Math.round(
    (scores.kcal + scores.fat + scores.carbs + scores.protein) / 4,
  );

  return (
    <div className="w-full flex flex-col gap-3">
      {/* Gesamtscore */}
      <div
        className={`rounded-card border p-3 flex items-center gap-4 ${scoreBg(overall)}`}
      >
        <div className={`text-xl font-black ${scoreColor(overall)}`}>
          {overall}
        </div>
        <div>
          <p className="text-xs font-semibold text-ink">Balance Score</p>
          <p className="text-xs text-ink-muted">
            {overall >= 75
              ? "Sehr ausgewogen"
              : overall >= 50
                ? "Gut"
                : "Verbesserungspotenzial"}
          </p>
        </div>
      </div>

      {/* Makro-Scores */}
      <div className="flex flex-col gap-1.5">
        {(["kcal", "fat", "carbs", "protein"] as const).map((key) => {
          const s = scores[key];
          const { label, color } = BALANCE_MACRO_LABELS[key];
          return (
            <div key={key} className="flex items-center gap-2">
              <span
                className="text-xs font-bold w-12 shrink-0"
                style={{ color }}
              >
                {label}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-surface-raised overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${s}%`, backgroundColor: color }}
                />
              </div>
              <span className="text-xs text-ink-muted w-8 text-right shrink-0">
                {s}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Haupt-Export ─────────────────────────────────────────────────────────────

export function AllNutriValues(props: {
  nutriValues: NutriValues[];
  needForDate?: NeedTimeslice;
  mode: "horizontal" | "vertical";
  trackingMode?: TrackingMode;
}) {
  const trackingMode = props.trackingMode ?? "restriction";
  const sum = props.nutriValues.reduce(nutriValueSumReducer, emptyNutriValue);
  const need = props.needForDate;

  if (trackingMode === "none") return null;

  // Ohne Bedarf gibt es keinen Vergleichswert (z. B. neu angelegte Person ohne
  // Bedarf). Nicht abstürzen und keinen 0-Wert vortäuschen, sondern hinweisen.
  if (!need) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 p-2 text-center">
        <span className="text-xs text-ink-muted">Kein Bedarf hinterlegt</span>
      </div>
    );
  }

  if (trackingMode === "flex") {
    return <FlexNutriValues sum={sum} need={need} />;
  }

  if (trackingMode === "balance") {
    return <BalanceNutriValues sum={sum} need={need} />;
  }

  // ── Restriktiv (bestehende Darstellung) ──────────────────────────────────
  const components = (
    <>
      <FlexMacroBar name={"fat"} value={sum.fat} target={need.fat} />
      <FlexMacroBar name={"carbs"} value={sum.carbs} target={need.carbs} />
      <FlexMacroBar
        name={"protein"}
        value={sum.protein}
        target={need.protein}
      />
    </>
  );

  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <NutriComponent
        variant={"circular"}
        name={"kcal"}
        value={sum.kcal}
        need={need.kcal}
      />
      {props.mode === "vertical" ? (
        <div className="flex w-full max-w-[250px] flex-col gap-1">
          {components}
        </div>
      ) : (
        <div className="flex w-full items-center gap-10">{components}</div>
      )}
    </div>
  );
}
